import axiosInstance from "./axiosInstance";

export interface HolidayApi {
  publicId: string;
  holidayName: string;
  holidayDate: string;
  holidayTypeId: number;
  holidayTypeName: string;
  isActive: boolean;
}

export interface HolidayFilters {
  searchTerm?: string;
  status?: boolean;
  pageNo?: number;
  pageSize?: number;
}

export interface HolidaySearchResponse {
  success: boolean;
  message: string;
  data: HolidaySearchPage;
  errors: unknown[];
}

export interface HolidaySearchPage {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  success: boolean;
  message: string;
  data: HolidayApi[];
  errors: unknown[];
}

export interface HolidayTypeApi {
  id: number;
  publicId: string;
  typeName: string;
  code: string;
  isActive: boolean;
}

export interface CreateHolidayPayload {
  holidayName: string;
  holidayDate: string;
  holidayTypeId: number;
  isActive: boolean;
}

// Helper to get token safely
export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// table listing api
export const getHolidays = async (
  filters: HolidayFilters = {},
): Promise<HolidaySearchResponse> => {
  try {
    const response = await axiosInstance.get<HolidaySearchResponse>(
      "/hr/settings/holiday-calendar/search",
      {
        params: {
          PageNumber: filters.pageNo ?? 1,
          PageSize: filters.pageSize ?? 20,
          HolidayName: filters.searchTerm || undefined,
          IsActive: filters.status ?? undefined,
        },
        headers: {
          ...getAuthHeader(),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch holiday types from the API dropdown
export const getHolidayTypes = async (): Promise<HolidayTypeApi[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: HolidayTypeApi[];
  }>("/hr/settings/holiday-types", { headers: getAuthHeader() });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(response.data.message || "Failed to load holiday types.");
  }
  return response.data.data;
};

// Create a new holiday entry in the API
export const createHoliday = async (payload: CreateHolidayPayload) => {
  const response = await axiosInstance.post<{
    success?: boolean;
    message?: string;
  }>("/hr/settings/holiday-calendar", payload, { headers: getAuthHeader() });
  if (response.data?.success === false) {
    throw new Error(response.data.message || "Failed to save holiday.");
  }
  return response.data;
};

// Update an existing holiday entry in the API
export const updateHoliday = async (
  publicId: string,
  payload: CreateHolidayPayload,
) => {
  const response = await axiosInstance.put<{
    success?: boolean;
    message?: string;
  }>(`/hr/settings/holiday-calendar/${encodeURIComponent(publicId)}`, payload, {
    headers: getAuthHeader(),
  });
  if (response.data?.success === false) {
    throw new Error(response.data.message || "Failed to update holiday.");
  }
  return response.data;
};

// Delete a holiday entry from the API
export const deleteHoliday = async (publicId: string) => {
  const response = await axiosInstance.delete<{
    success?: boolean;
    message?: string;
  }>(`/hr/settings/holiday-calendar/${encodeURIComponent(publicId)}`, {
    headers: getAuthHeader(),
  });
  if (response.data?.success === false) {
    throw new Error(response.data.message || "Failed to delete holiday.");
  }
  return response.data;
};
